import React, { createContext, useContext, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, Share2, X } from "lucide-react";
import { toast } from "sonner";
import { buildShareUrl } from "../lib/share";

/* ============================================================
   Global Share Sheet
   ============================================================
   Exactly 5 options — all chosen for reliability on mobile web,
   WITHOUT requiring clipboard permissions:
     1. WhatsApp     — whatsapp://send?text= (native intent)
     2. Facebook     — sharer.php (direct web dialog, no clipboard)
     3. Pinterest    — pin/create/button (direct web dialog, no clipboard)
     4. More options — navigator.share Web Share API (phone-native sheet)
     5. Copy Link    — clipboard with graceful fallback ("Tap and hold to copy")
   ============================================================ */

/* ---------- BRAND ICONS (inline SVG, no extra deps) ---------- */

const WhatsAppIcon = () => (
  <svg viewBox="0 0 32 32" width="22" height="22" fill="#fff" aria-hidden>
    <path d="M16 3C9.4 3 4 8.4 4 15c0 2.4.7 4.7 1.9 6.7L4 29l7.5-2c1.9 1 4.1 1.6 6.5 1.6h.1c6.6 0 12-5.4 12-12s-5.4-12-12-12h-2.1zm-1 5c.4 0 .8 0 1.1.1 1.2.3 3 3.3 3.2 3.6.2.4.4.8.1 1.3-.3.5-.5.8-1 .9-.5.1-.7.2-1 0-.3-.2-1.4-.5-2.6-1.7-1-.9-1.6-2-1.8-2.3-.2-.3 0-.5.1-.6.1-.1.3-.4.5-.5.2-.2.3-.3.4-.5.1-.2 0-.4-.1-.5-.1-.2-.7-1.7-.9-2.3-.2-.5-.5-.5-.7-.5z" />
  </svg>
);

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="#fff" aria-hidden>
    <path d="M12 2C6.5 2 2 6.5 2 12c0 5 3.7 9.1 8.4 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.3c-1.3 0-1.7.8-1.7 1.6V12h2.8l-.4 2.9h-2.4v7C18.3 21.1 22 17 22 12c0-5.5-4.5-10-10-10z"/>
  </svg>
);

const PinterestIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="#fff" aria-hidden>
    <path d="M12 2C6.5 2 2 6.5 2 12c0 4.2 2.6 7.8 6.3 9.3-.1-.8-.2-2 0-2.8.2-.8 1.2-5.1 1.2-5.1s-.3-.6-.3-1.5c0-1.4.8-2.5 1.9-2.5.9 0 1.3.7 1.3 1.5 0 .9-.6 2.3-.9 3.6-.3 1.1.5 2 1.6 2 1.9 0 3.4-2 3.4-5 0-2.6-1.9-4.4-4.6-4.4-3.1 0-5 2.3-5 4.7 0 .9.4 1.9.8 2.5.1.1.1.2.1.3-.1.3-.3 1.1-.3 1.3-.1.2-.2.3-.4.2-1.4-.7-2.3-2.7-2.3-4.4 0-3.6 2.6-6.9 7.5-6.9 3.9 0 7 2.8 7 6.5 0 3.9-2.5 7.1-5.9 7.1-1.2 0-2.3-.6-2.6-1.3 0 0-.6 2.2-.7 2.7-.3 1-1 2.2-1.4 3 1 .3 2.1.5 3.2.5 5.5 0 10-4.5 10-10S17.5 2 12 2z"/>
  </svg>
);

/* ---------- Context ---------- */

const ShareSheetCtx = createContext(null);

export function useShareSheet() {
  return useContext(ShareSheetCtx);
}

export function ShareSheetProvider({ children }) {
  const [state, setState] = useState({ open: false, payload: null });
  const api = useRef({
    open: (payload) => setState({ open: true, payload }),
    close: () => setState({ open: false, payload: null }),
  });
  return (
    <ShareSheetCtx.Provider value={api.current}>
      {children}
      <ShareSheet
        open={state.open}
        payload={state.payload}
        onClose={api.current.close}
      />
    </ShareSheetCtx.Provider>
  );
}

// Backwards-compat alias — AppShell + earlier callers import `ShareProvider`.
export const ShareProvider = ShareSheetProvider;

/* ---------- Message builders ---------- */

function buildMessage(payload) {
  if (!payload) return "Check out Glow & Grow ✨ a luxury beauty self-growth app I'm loving.";
  const { type, payload: p } = payload;
  if (type === "article") {
    return `📖 Reading "${p.title}" on Glow & Grow — a luxury beauty growth app for all skin tones and textures.`;
  }
  if (type === "recipe") {
    return `🧪 Just tried "${p.title}" — a DIY beauty recipe on Glow & Grow. Adding it to my ritual.`;
  }
  if (type === "challenge") {
    return `🏆 Day ${p.day || 1} of the ${p.title || "Glow"} challenge on Glow & Grow. Join me?`;
  }
  if (type === "dupe") {
    return `💎 Found a dupe for ${p.original || "my fave product"} on Glow & Grow's Dupe Finder. Game changer.`;
  }
  if (type === "report") {
    return `✨ Just got my personal Glow Report on Glow & Grow — luxury beauty intelligence built for me.`;
  }
  return "Check out Glow & Grow ✨ a luxury beauty self-growth app I'm loving.";
}

/* ---------- Sheet ---------- */

function ShareSheet({ open, payload, onClose }) {
  const message = buildMessage(payload);
  // Deep-link URL per content type (article/recipe/challenge/dupe/report).
  // Always rooted on https://glowgrow.app — never window.location.
  const url = buildShareUrl(payload);
  const encMsg = encodeURIComponent(message);
  const encUrl = encodeURIComponent(url);

  const openInNewTab = (href) => {
    // noopener noreferrer is critical — `window.open` without these leaks the
    // referrer and gives the opened window access to `window.opener`.
    const win = window.open(href, "_blank", "noopener,noreferrer");
    if (win) win.opener = null;
  };

  // 1. WhatsApp — works as native intent on mobile, web fallback on desktop.
  const onWhatsApp = () => {
    const isMobile = /android|iphone|ipad|ipod/i.test(navigator.userAgent);
    const href = isMobile
      ? `whatsapp://send?text=${encMsg}%20${encUrl}`
      : `https://wa.me/?text=${encMsg}%20${encUrl}`;
    openInNewTab(href);
    onClose();
  };

  // 2. Facebook — direct share dialog, no clipboard.
  const onFacebook = () => {
    openInNewTab(
      `https://www.facebook.com/sharer/sharer.php?u=${encUrl}&quote=${encMsg}`,
    );
    onClose();
  };

  // 3. Pinterest — direct save dialog, no clipboard.
  const onPinterest = () => {
    openInNewTab(
      `https://pinterest.com/pin/create/button/?url=${encUrl}&description=${encMsg}`,
    );
    onClose();
  };

  // 4. Native Share API — opens the phone's built-in share sheet which has
  //    TikTok, Instagram, Messages, Mail, etc.
  const onNative = async () => {
    if (!navigator.share) {
      toast("Native share not available on this browser");
      return;
    }
    try {
      await navigator.share({
        title: "Glow & Grow",
        text: message,
        url,
      });
      onClose();
    } catch (_) {
      // User cancelled — silent.
    }
  };

  // 5. Copy Link — URL ONLY, graceful fallback if clipboard blocked.
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied!");
      onClose();
    } catch (_) {
      toast("Tap and hold to copy", {
        description: url,
        duration: 6000,
      });
    }
  };

  const showNative =
    typeof navigator !== "undefined" && typeof navigator.share === "function";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-cocoa/40 backdrop-blur-[2px] flex items-end justify-center sm:items-center p-0 sm:p-4"
          onClick={onClose}
          data-testid="share-sheet-backdrop"
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 32 }}
            className="bg-cream w-full sm:max-w-sm rounded-t-3xl sm:rounded-3xl shadow-floating p-5 pt-6 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="share-sheet"
          >
            {/* Handle bar */}
            <div className="sm:hidden mx-auto w-10 h-1 bg-cocoa/15 rounded-full mb-4" />

            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-rose font-medium">
                  Share the glow
                </p>
                <h3 className="font-serif text-2xl text-cocoa leading-tight">
                  Spread the ritual
                </h3>
              </div>
              <button
                onClick={onClose}
                data-testid="share-sheet-close"
                className="w-8 h-8 rounded-full bg-white border border-[#f0e8e4] flex items-center justify-center"
                aria-label="Close"
              >
                <X size={14} className="text-cocoa/55" />
              </button>
            </div>

            <p className="text-sm text-cocoa/65 font-light mb-5 leading-relaxed">
              Send to a friend, save for inspiration, or open your phone&apos;s share sheet.
            </p>

            <div className="grid grid-cols-4 gap-3">
              <ShareTile
                testId="share-whatsapp"
                onClick={onWhatsApp}
                bg="#25D366"
                label="WhatsApp"
                icon={<WhatsAppIcon />}
              />
              <ShareTile
                testId="share-facebook"
                onClick={onFacebook}
                bg="#1877F2"
                label="Facebook"
                icon={<FacebookIcon />}
              />
              <ShareTile
                testId="share-pinterest"
                onClick={onPinterest}
                bg="#E60023"
                label="Pinterest"
                icon={<PinterestIcon />}
              />
              {showNative && (
                <ShareTile
                  testId="share-more"
                  onClick={onNative}
                  bg="#2E1608"
                  label="More"
                  icon={<Share2 size={20} className="text-white" />}
                />
              )}
              <ShareTile
                testId="share-copy"
                onClick={onCopy}
                bg="#C9975A"
                label="Copy link"
                icon={<Link size={20} className="text-white" />}
                className={showNative ? "" : "col-start-4"}
              />
            </div>

            <p className="text-[11px] text-cocoa/45 font-light text-center mt-5 leading-relaxed">
              {showNative
                ? "Use “More” to share to TikTok, Instagram, Messages and any installed app."
                : "Your browser doesn&apos;t support native sharing — use Copy link instead."}
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function ShareTile({ icon, label, bg, onClick, testId, className = "" }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`flex flex-col items-center gap-1.5 ${className}`}
    >
      <span
        className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-card"
        style={{ background: bg }}
      >
        {icon}
      </span>
      <span className="text-[11px] text-cocoa/70 font-medium">{label}</span>
    </button>
  );
}
